import React, { useEffect, useState } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import { Chat } from "@/types/chat";
import { ItemDropdownMenu } from "./ItemDropdownMenu";
import { useItemManagement } from "@/hooks/useItemManagement";
import { ItemActions } from "./ItemActions";
import { Button } from "@/components/ui/button";
import { GroupedItemsList } from "./GroupedItemsList";
import { MessageSquare, Pin } from "lucide-react";
import MiniSidebarSection from "./MiniSidebarSection";
import {
  useAuthenticationContext,
  useCurrentAccountId,
} from "@/store/authContext/authContext";
import EventBus from "@/lib/eventBus";
import mixpanel from "@/lib/mixpanel";
import { updateChatPinned } from "@/lib/serverComm";

interface SidebarChatSectionProps {
  isExpanded: boolean;
  onToggle: (value: boolean) => void;
  onModalStateChange: (isOpen: boolean) => void;
  isActive: boolean;
  isMiniMode: boolean;
}

const SidebarChatSection: React.FC<SidebarChatSectionProps> = ({
  isExpanded,
  onToggle,
  onModalStateChange,
  isActive,
  isMiniMode,
}) => {
  const location = useLocation(),
    navigate = useNavigate();
  const matchCurrentChat = useMatch("/chat/:id");
  const [isPopoverOpen] = useState(false);
  const currentAccountId = useCurrentAccountId();
  const { state } = useAuthenticationContext();

  const chats = state.chatState.sidebarChats.data;
  const params = state.chatState.sidebarChats.params;

  const refreshChats = () => {
    EventBus.$emit("sidebar:refreshChats");
  };

  useEffect(() => {
    refreshChats();
  }, [params.query, currentAccountId]);

  const {
    selectedItem: selectedChat,
    setSelectedItem: setSelectedChat,
    isRenameOpen,
    setIsRenameOpen,
    isDeleteOpen,
    setIsDeleteOpen,
    handleRename,
    handleDelete,
  } = useItemManagement("chats", refreshChats, "/new-chat", "chat");

  useEffect(() => {
    setIsRenameOpen(false);
    setSelectedChat(null);
  }, [location, setIsRenameOpen, setSelectedChat]);

  // Update parent modal state when either popover or dialogs are open
  useEffect(() => {
    onModalStateChange(isPopoverOpen || isRenameOpen || isDeleteOpen);
  }, [isPopoverOpen, isRenameOpen, isDeleteOpen, onModalStateChange]);

  const handleNewChatClick = () => {
    EventBus.$emit("chat:resetChat");
    mixpanel.track("Click", {
      eventDescription: "Chats > New Thread",
    });
    navigate("/new-chat");
  };

  const handlePin = async (chat: Chat) => {
    try {
      await updateChatPinned(chat.id, !chat.pinned);
      refreshChats();
    } catch (error) {
      console.error("Error updating chat pin status:", error);
    }
  };

  const renderChatItem = (chat: Chat, isActive?: boolean): React.ReactNode => {
    const url =
      chat.deep_insight_id === undefined || chat.deep_insight_id === null
        ? `/chat/${chat.id}`
        : `/deep-insights/${chat.deep_insight_id}/${chat.id}`;

    return (
      <div key={chat.id} className="flex items-center group">
        <Link
          to={url}
          className={`flex-1 min-w-0 ${isActive ? "text-accent-foreground" : ""}`}
        >
          <Button
            variant="ghost"
            className={`w-full justify-start px-2 py-1.5 h-auto text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
              isActive
                ? "font-bold text-foreground dark:text-foreground bg-sky-50 dark:bg-sky-900/20"
                : ""
            }`}
            title={chat.title}
            onClick={() =>
              mixpanel.track("Click", {
                eventDescription: `Chats > ${chat.title}`,
              })
            }
          >
            {chat.pinned && (
              <Pin
                size={14}
                className="mr-1.5 flex-shrink-0 text-gray-500 dark:text-gray-400"
              />
            )}
            <div className="truncate">{chat.title}</div>
          </Button>
        </Link>
        <ItemDropdownMenu
          config={{
            isDuplicateEnabled: false,
            isDeleteEnabled: true,
            isRenameEnabled: true,
            isPinEnabled: true,
          }}
          isPinned={chat.pinned}
          onPin={() => handlePin(chat)}
          onRename={() => {
            setSelectedChat(chat);
            setIsRenameOpen(true);
          }}
          onDelete={() => {
            setSelectedChat(chat);
            setIsDeleteOpen(true);
          }}
        />
      </div>
    );
  };

  const chatContent = (
    <>
      <div className="block mb-1 px-6 block mb-1">
        <Button
          onClick={handleNewChatClick}
          variant="ghost"
          className={`my-4 px-6 py-2 w-full h-auto rounded-full border-primary border hover:bg-secondary/5 flex justify-center items-center transition-colors duration-200`}
        >
          <span className="font-bold text-[13px] bg-gradient-to-r from-gradient-color-main-end to-gradient-color-main-start text-transparent bg-clip-text">
            NEW THREAD
          </span>
        </Button>
      </div>

      <GroupedItemsList
        items={chats}
        renderItem={renderChatItem}
        viewAllLink="/chats"
        viewAllText="View All Threads"
        activeItemId={matchCurrentChat?.params.id}
        viewAllTrackingName="Chats > View All Threads"
      />
    </>
  );

  const itemActions = (
    <ItemActions
      isRenameOpen={isRenameOpen}
      isDeleteOpen={isDeleteOpen}
      itemType="Chat"
      onRenameClose={() => setIsRenameOpen(false)}
      onDeleteClose={() => setIsDeleteOpen(false)}
      onRename={handleRename}
      onDelete={handleDelete}
      initialTitle={selectedChat?.title}
      onModalStateChange={onModalStateChange}
    />
  );

  if (isMiniMode) {
    return (
      <>
        <MiniSidebarSection
          icon={<MessageSquare size={20} />}
          label="Chats"
          isActive={isActive}
        >
          {chatContent}
        </MiniSidebarSection>
        {itemActions}
      </>
    );
  }

  return (
    <>
      <SidebarItem
        icon={
          <MessageSquare
            size={20}
            color={
              isActive
                ? "var(--sidebar-active-color)"
                : "var(--sidebar-item-color)"
            }
          />
        }
        label="Chats"
        isExpanded={isExpanded}
        onToggle={onToggle}
        isActive={isActive}
      >
        {chatContent}
      </SidebarItem>
      {itemActions}
    </>
  );
};

export default SidebarChatSection;
